import { useCallback, useState } from 'react';

import { useRoleContext } from './role-context';
import {
  CandidateRole,
  getSentInterviewLink,
  getSentScreeningLink,
} from './role-types';

export interface BookingLauncher {
  bookInterview: (role: CandidateRole) => Promise<void>;
  bookScreening: (role: CandidateRole) => Promise<void>;
  bookingRoleId: string | null;
  error: string | null;
  clearError: () => void;
}

const BOOKING_ERROR =
  "We couldn't get your booking link right now. Please try again.";

export const useBookingLauncher = (): BookingLauncher => {
  const { requestBookingLink } = useRoleContext();
  const [bookingRoleId, setBookingRoleId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const openBookingLink = useCallback(
    async (roleId: string, resolveLink: () => Promise<string | null>) => {
      if (bookingRoleId) return;
      setError(null);
      setBookingRoleId(roleId);

      const calendlyTab = window.open('', '_blank');
      try {
        const link = await resolveLink();
        if (link) {
          if (calendlyTab) {
            calendlyTab.opener = null;
            calendlyTab.location.href = link;
          } else {
            window.open(link, '_blank', 'noopener');
          }
          return;
        }
        calendlyTab?.close();
        setError(BOOKING_ERROR);
      } catch {
        calendlyTab?.close();
        setError(BOOKING_ERROR);
      } finally {
        setBookingRoleId(null);
      }
    },
    [bookingRoleId]
  );

  const bookInterview = useCallback(
    (role: CandidateRole) =>
      openBookingLink(
        role.id,
        async () =>
          getSentInterviewLink(role) ?? (await requestBookingLink(role.id))
      ),
    [openBookingLink, requestBookingLink]
  );

  const bookScreening = useCallback(
    (role: CandidateRole) => {
      const sentLink = getSentScreeningLink(role);
      return sentLink
        ? openBookingLink(role.id, () => Promise.resolve(sentLink))
        : bookInterview(role);
    },
    [bookInterview, openBookingLink]
  );

  return {
    bookInterview,
    bookScreening,
    bookingRoleId,
    error,
    clearError: () => setError(null),
  };
};
